import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { getUrlParamsFetch, handleAxiosError } from '@/shared';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  UseFetchEnabledParams,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { isAxiosError } from 'axios';
import { IPreagendamientoPymes, IPreagendamientoPymesForm, IPreagendamientoPymesParams, PreagendamientosPymesPagingRes } from '@/shared/interfaces/erp/ventas_pymes/preagendamiento_pymes.interface';

interface IPreagendamientoPost {
  id: string,
  data: IPreagendamientoPymesForm
}

const { get, post, put, remove } = erpApi();

///* tanStack query
export const useFetchPreagendamientosPymes = ({
  enabled = true,
  params,
}: UseFetchEnabledParams<IPreagendamientoPymesParams> = {}) => {
  return useQuery({
    queryKey: ['preagendamientopymes', ...Object.values(params ?? {})],
    queryFn: () => getPreagendamientosPymes(params),
    enabled: enabled,
    refetchOnWindowFocus: false,
  });
};

export const useFetchGetPreagendamientoPymesById = (id: string) => {
  return useQuery({
    queryKey: ['preagendamientopymes', id],
    queryFn: () => getPreagendamientoPymesById(id),
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useFetchPostPreagendamientoPymes = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: IPreagendamientoPymesForm) =>
      postPreagendamientoPymes(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preagendamientopymes'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Preagendamiento creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {

      if (isAxiosError(error)) {
        const respError: any = error?.response?.data || {};
        switch (respError.code) {
          case 408:
            navigate && returnUrl && navigate(returnUrl)
            setAlert(true, 'El preagendamiento se ha creado correctamente. [408]', AlertSeverityEnum.success)
            break
          default:

            enableErrorRedirect &&
              navigate &&
              returnUrl &&
              navigate(returnErrorUrl ?? (returnUrl || ''));

            handleAxiosError(error, customMessageErrorAlert)
            break;
        }
      }
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};
export const useFetchPutPreagendamientoPymes = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,

  customSuccessCallback,
}: UseMutationsParams = {}) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: (params: IPreagendamientoPost) =>
      putPreagendamientoPymes(params),

    onSuccess: res => {
      queryClient.invalidateQueries({ queryKey: ['preagendamientopymes'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Preagendamiento actualizada correctamente',
          AlertSeverityEnum.success
        );

      customSuccessCallback && customSuccessCallback(res?.data);
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios

export const getPreagendamientosPymes = async (
  params?: IPreagendamientoPymesParams
) => {
  const stateParams = { ...params };

  const queryParams = getUrlParamsFetch(stateParams);

  return get<PreagendamientosPymesPagingRes>(
    `${urlBackend}api/v1/preagendamientopymes/?${queryParams}`,
    true
  );
};

export const getPreagendamientoPymesById = async (id: string) => {
  try {
    return await get<IPreagendamientoPymes>(
      `${urlBackend}api/v1/preagendamientopymes/${id}`,
      true
    );
  } catch (error) {
    handleAxiosError(error);
  }
};

export const postPreagendamientoPymes = async (
  data: IPreagendamientoPymesForm
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return post<IPreagendamientoPymes>(
    `${urlBackend}api/v1/preagendamientopymes/`,
    data,
    true
  );
};

export const putPreagendamientoPymes = async ({
  id,
  data,
}: IPreagendamientoPost) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);

  return put<IPreagendamientoPymes>(
    `${urlBackend}api/v1/preagendamientopymes/${id}/`,
    data,
    true
  );
};

export const deletePreagendamiento = async (id: string) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true);
  return remove<IPreagendamientoPymes>(
    `${urlBackend}api/v1/preagendamientopymes/${id}/`,
    true
  );
};


///* Estado del controto
export const useRevisarContrato = ({ queryKey = 'contrato' }: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: postRevisarContrato,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setAlert(
        true,
        'Contrato fue revisado.',
        AlertSeverityEnum.success
      );
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
    onError: error => {
      handleAxiosError(error);
    },
  });
};

export const postRevisarContrato = async (id_preagendamiento: string) => {
  /* const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;

  setIsGlobalLoading(true); */
  return post<any>(
    `${urlBackend}api/v1/enviar-revisar-contrato/${id_preagendamiento}/`, {},
    true
  );
};
